'use client'

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import dynamic from 'next/dynamic'
import AppTooltipWrapper from './molecules/AppTooltipWrapper'
import { TYPES } from './AppIcon'
import { withRequiredAsterisk } from '@/app/utils/required-label'
import { foldForSearch } from '@/app/utils/helpers'

const CountryFlag = dynamic(() => import('@/app/components/atomic/CountryFlag'), { ssr: false })

interface SelectOption {
    label: string
    value: string | number
    code?: string
    frenchTerritory?: boolean
}

interface AppSelectProps {
    label: string
    error?: { message?: string }
    options: SelectOption[]
    placeholder?: string
    wrapperClassName?: string
    className?: string
    id?: string
    tooltip?: {
        title?: string
        text: string
        iconClassName?: string
        iconType?: keyof typeof TYPES
    }
    variant?: 'default' | 'loan' | 'health' | 'v2' | 'travel'
    value?: string | number
    onChange?: (event: React.ChangeEvent<HTMLSelectElement> | { target: { value: string | number; name?: string; valueAsNumber?: number } }) => void
    onBlur?: (event?: any) => void
    name?: string
    disabled?: boolean
    searchable?: boolean
    /** Font Awesome regular icon shown before the value, e.g. `fa-clock` */
    icon?: string
}

const AppSelect = React.forwardRef<HTMLSelectElement, AppSelectProps>(
    (
        {
            label,
            error,
            options,
            placeholder = 'Choisissez...',
            tooltip,
            wrapperClassName = '',
            className = '',
            id,
            variant = 'default',
            value,
            onChange,
            onBlur,
            name,
            disabled = false,
            searchable = false,
            icon,
        },
        ref
    ) => {
        const isLoan = variant === 'loan'
        const isHealth = variant === 'health'
        const isV2 = variant === 'v2'
        const isTravel = variant === 'travel'
        const [isOpen, setIsOpen] = useState(false)
        const [highlightedIndex, setHighlightedIndex] = useState(-1)
        const [searchTerm, setSearchTerm] = useState('')
        const [internalValue, setInternalValue] = useState<string | number>(value ?? '')
        const containerRef = useRef<HTMLDivElement>(null)
        const listRef = useRef<HTMLDivElement>(null)
        const searchInputRef = useRef<HTMLInputElement>(null)
        const hiddenSelectRef = useRef<HTMLSelectElement | null>(null)

        const combinedRef = useCallback(
            (el: HTMLSelectElement | null) => {
                hiddenSelectRef.current = el
                if (typeof ref === 'function') ref(el)
                else if (ref) (ref as React.MutableRefObject<HTMLSelectElement | null>).current = el
            },
            [ref]
        )

        useEffect(() => {
            if (value !== undefined) {
                setInternalValue(value)
            }
        }, [value])

        // Sync internal value from hidden select once (for register mode where RHF sets value via ref)
        const syncedRef = useRef(false)
        useEffect(() => {
            if (syncedRef.current) return
            const select = hiddenSelectRef.current
            if (select && value === undefined && select.value) {
                syncedRef.current = true
                setInternalValue(select.value)
            }
        })

        const currentValue = value !== undefined ? value : internalValue
        const selectedOption = useMemo(
            () => options.find(o => String(o.value) === String(currentValue)),
            [options, currentValue]
        )
        const displayValue = selectedOption?.label || ''

        const visibleOptions = useMemo(() => {
            if (!searchable || !searchTerm) return options
            const needle = foldForSearch(searchTerm)
            return options.filter(o => foldForSearch(o.label).includes(needle))
        }, [options, searchable, searchTerm])

        const borderClass = error
            ? 'border-invalidRed'
            : isV2
            ? `border-rm-gray-light ${isOpen ? 'border-rm-gray' : 'hover:border-rm-gray'}`
            : isTravel
            ? `border-rm-gray-light ${isOpen ? 'border-rm-blue' : 'hover:border-rm-blue'}`
            : isLoan || isHealth
            ? `border-blue-300 ${isOpen ? 'border-blue-500' : 'hover:border-blue-500'}`
            : 'border-gray-300'

        const close = useCallback(() => {
            const wasOpen = isOpen
            setIsOpen(false)
            setHighlightedIndex(-1)
            setSearchTerm('')
            // Only trigger onBlur if the dropdown was actually open (user interacted with it)
            // This prevents premature validation on fields the user never touched
            if (wasOpen && hiddenSelectRef.current) {
                onBlur?.({ target: hiddenSelectRef.current, type: 'blur' } as any)
            }
        }, [isOpen, onBlur])

        useEffect(() => {
            const handleClickOutside = (e: MouseEvent) => {
                if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                    close()
                }
            }
            document.addEventListener('mousedown', handleClickOutside)
            return () => document.removeEventListener('mousedown', handleClickOutside)
        }, [close])

        useEffect(() => {
            if (isOpen && highlightedIndex >= 0 && listRef.current) {
                const item = listRef.current.children[highlightedIndex] as HTMLElement
                item?.scrollIntoView({ block: 'nearest' })
            }
        }, [highlightedIndex, isOpen])

        useEffect(() => {
            if (isOpen && searchable) {
                searchInputRef.current?.focus()
            }
        }, [isOpen, searchable])

        const handleSelect = (option: SelectOption) => {
            setInternalValue(option.value)

            // Update hidden select value for react-hook-form compatibility
            const select = hiddenSelectRef.current
            if (select) {
                const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
                    window.HTMLSelectElement.prototype,
                    'value'
                )?.set
                nativeInputValueSetter?.call(select, String(option.value))
                const event = new Event('change', { bubbles: true })
                select.dispatchEvent(event)
            }

            // Also call onChange directly for controlled mode
            if (onChange) {
                const numVal = Number(option.value)
                onChange({
                    target: {
                        value: option.value,
                        name: name || '',
                        valueAsNumber: isNaN(numVal) ? NaN : numVal,
                    },
                } as unknown as React.ChangeEvent<HTMLSelectElement>)
            }

            close()
        }

        const handleKeyDown = (e: React.KeyboardEvent) => {
            if (disabled) return
            switch (e.key) {
                case 'Enter':
                case ' ':
                    e.preventDefault()
                    if (!isOpen) {
                        setIsOpen(true)
                        setHighlightedIndex(visibleOptions.findIndex(o => String(o.value) === String(currentValue)))
                    } else if (highlightedIndex >= 0 && visibleOptions[highlightedIndex]) {
                        handleSelect(visibleOptions[highlightedIndex])
                    }
                    break
                case 'ArrowDown':
                    e.preventDefault()
                    if (!isOpen) {
                        setIsOpen(true)
                    }
                    setHighlightedIndex(prev => Math.min(prev + 1, visibleOptions.length - 1))
                    break
                case 'ArrowUp':
                    e.preventDefault()
                    setHighlightedIndex(prev => Math.max(prev - 1, 0))
                    break
                case 'Escape':
                    // Modal shells close on a document-level Escape: an open list swallows the key first
                    if (isOpen) e.stopPropagation()
                    close()
                    break
            }
        }

        // The search input only forwards navigation keys (space must remain typable)
        const handleSearchKeyDown = (e: React.KeyboardEvent) => {
            if (['ArrowDown', 'ArrowUp', 'Enter', 'Escape'].includes(e.key)) {
                handleKeyDown(e)
            }
        }

        return (
            <div className={`form-group ${wrapperClassName}`}>
                {label && (
                    <>
                        {tooltip ? (
                            <AppTooltipWrapper
                                id={id || label}
                                title={tooltip.title}
                                text={tooltip.text}
                                iconClassName={tooltip.iconClassName || 'fa-info-circle'}
                                iconType={tooltip.iconType || 'regular'}
                                theme={isLoan ? 'loan' : undefined}
                            >
                                <label htmlFor={id} className={`${isHealth ? 'text-[12px] font-semibold text-blue-800' : `text-[14px] font-medium ${isLoan ? 'text-blue-900' : 'text-gray-700'}`} p-1`}>
                                    {withRequiredAsterisk(label)}
                                </label>
                            </AppTooltipWrapper>
                        ) : (
                            <label htmlFor={id} className={`block text-[14px] font-medium ${isLoan ? 'text-blue-900' : 'text-gray-700'} p-1`}>
                                {withRequiredAsterisk(label)}
                            </label>
                        )}
                    </>
                )}

                {/* Hidden native select for react-hook-form compatibility */}
                <select
                    ref={combinedRef}
                    id={id ? `${id}-hidden` : undefined}
                    name={name}
                    value={String(currentValue)}
                    onChange={() => {}}
                    className="sr-only"
                    tabIndex={-1}
                    aria-hidden="true"
                >
                    {placeholder && <option value="">{placeholder}</option>}
                    {options.map((option, index) => (
                        <option key={`${option.value}-${index}`} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>

                {/* Custom dropdown */}
                <div ref={containerRef} className={`relative ${className}`}>
                    <button
                        type="button"
                        id={id}
                        onClick={() => !disabled && setIsOpen(!isOpen)}
                        onKeyDown={handleKeyDown}
                        disabled={disabled}
                        className={`w-full flex items-center justify-between px-3 py-2 border bg-white text-left
                            transition-all duration-300 focus:outline-none
                            ${isOpen
                                ? `rounded-b-none ${isTravel ? 'rounded-t-xl' : 'rounded-t-rm-10'}`
                                : isTravel ? 'rounded-xl' : 'rounded-rm-10'}
                            ${borderClass}
                            ${isHealth || isV2 || isLoan ? 'min-h-12' : ''}
                            ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}
                        `}
                        style={isHealth || isV2 || isLoan ? undefined : isTravel ? { minHeight: '52px' } : { minHeight: '34px' }}
                    >
                        <span className={`flex min-w-0 items-center gap-2 text-[14px] ${
                            displayValue
                                ? (isLoan ? 'text-blue-700 font-medium' : isHealth ? 'text-blue-900 font-semibold' : isV2 ? 'text-gray-900 font-normal' : isTravel ? 'text-pale font-normal' : 'text-gray-900 font-semibold')
                                : 'text-[#B3B3B3] font-normal'
                        }`}>
                            {icon && <i className={`fa-regular ${icon} shrink-0 text-[16px] text-[#2D3838]`} aria-hidden />}
                            {selectedOption?.code && (
                                <CountryFlag code={selectedOption.code} frenchTerritory={selectedOption.frenchTerritory} />
                            )}
                            <span className="truncate">{displayValue || placeholder}</span>
                        </span>
                        {isTravel ? (
                            <i
                                className={`fa-regular fa-chevron-down shrink-0 ml-2 text-[13px] text-[#6b7280] transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                                aria-hidden
                            />
                        ) : (
                            <svg
                                className={`w-3.5 h-3.5 shrink-0 ml-2 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}
                                    ${isLoan ? 'text-blue-700' : isHealth ? 'text-blue-600' : 'text-gray-500'}`}
                                fill="currentColor"
                                viewBox="0 0 20 20"
                            >
                                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                        )}
                    </button>

                    {/* Dropdown list */}
                    {isOpen && (
                        <div
                            className={`absolute z-50 w-[calc(100%+2px)] -left-[1px] bg-white border border-t-0 overflow-hidden ${isTravel ? 'rounded-b-xl' : 'rounded-b-rm-10'}
                                ${isV2 ? 'border-rm-gray-light' : isTravel ? 'border-[#e5e7eb]' : isLoan || isHealth ? 'border-blue-300' : 'border-gray-300'}
                            `}
                            style={{ top: 'calc(100%)' }}
                        >
                            {searchable && (
                                <div className="border-b border-gray-100 p-2">
                                    <input
                                        ref={searchInputRef}
                                        type="text"
                                        value={searchTerm}
                                        placeholder="Rechercher..."
                                        className="w-full rounded-rm-10 border border-gray-200 px-2 py-1.5 text-[14px] focus:outline-none"
                                        onChange={(e) => {
                                            setSearchTerm(e.target.value)
                                            setHighlightedIndex(-1)
                                        }}
                                        onKeyDown={handleSearchKeyDown}
                                    />
                                </div>
                            )}
                            <div ref={listRef} className="overflow-auto rounded-b-rm-10" style={{ maxHeight: '250px' }}>
                            {searchable && visibleOptions.length === 0 && (
                                <div className="px-3 py-[6px] text-[14px] text-gray-400">Aucun résultat</div>
                            )}
                            {visibleOptions.map((option, index) => {
                                const isSelected = String(option.value) === String(currentValue)
                                const isHighlighted = index === highlightedIndex
                                return (
                                    <div
                                        key={`${option.value}-${index}`}
                                        onClick={() => handleSelect(option)}
                                        onMouseEnter={() => setHighlightedIndex(index)}
                                        className={`flex items-center gap-2 px-3 py-[6px] cursor-pointer text-[14px] transition-colors duration-100
                                            ${isV2 ? 'font-normal' : isLoan ? 'font-medium' : 'font-semibold'}
                                            ${isHighlighted
                                                ? 'bg-[#0E69C9] text-white'
                                                : isSelected
                                                    ? (isLoan ? 'bg-blue-100 text-blue-700' : 'bg-blue-50 text-blue-900')
                                                    : (isLoan ? 'text-blue-700' : 'text-gray-900')
                                            }
                                        `}
                                    >
                                        {option.code && <CountryFlag code={option.code} frenchTerritory={option.frenchTerritory} />}
                                        {option.label}
                                    </div>
                                )
                            })}
                            </div>
                        </div>
                    )}
                </div>

                {error?.message && <p className="mt-1 text-[12px] text-red-600">{error.message}</p>}
            </div>
        )
    }
)

AppSelect.displayName = 'AppSelect'

export default AppSelect
